import React from 'react';
import { useTranslation } from 'react-i18next';

export const WhitepaperPage: React.FC = () => {
    const { t } = useTranslation();

    // Helper to render section dividers
    const Divider = () => (
        <div className="flex justify-center my-16">
            <div className="h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent w-full max-w-2xl"></div>
        </div>
    );

    return (
        <div className="min-h-screen relative overflow-hidden py-20 bg-bg-primary">
            {/* Background Elements */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-900/5 rounded-full blur-3xl"></div>
            </div>

            <div className="container mx-auto px-4 relative z-10 max-w-4xl">

                {/* Header */}
                <div className="text-center mb-20 animate-fade-in">
                    <div className="hero-badge inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
                        <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('pages.whitepaper.badge')}</span>
                    </div>
                    <h1 className="text-4xl md:text-6xl font-black text-white mb-6 bg-gradient-to-r from-gold to-white bg-clip-text text-transparent">
                        {t('pages.whitepaper.title')}
                    </h1>
                    <p className="text-xl md:text-2xl text-gray-300 mx-auto leading-relaxed font-light">
                        {t('pages.whitepaper.subtitle')}
                    </p>
                </div>

                {/* Executive Summary */}
                <div className="bg-surface/50 backdrop-blur-sm border border-gold/10 rounded-3xl p-8 md:p-12 mb-16 shadow-lg">
                    <h2 className="text-3xl font-bold text-white mb-6 border-l-4 border-gold pl-4">{t('pages.whitepaper.sections.executiveSummary.title')}</h2>
                    <div className="space-y-4 text-gray-300 leading-relaxed text-lg">
                        {(t('pages.whitepaper.sections.executiveSummary.content', { returnObjects: true }) as string[]).map((paragraph, idx) => (
                            <p key={idx}>{paragraph}</p>
                        ))}
                    </div>
                </div>

                <Divider />

                {/* 1. Problem Statement */}
                <div className="mb-16">
                    <h2 className="text-3xl font-bold text-gold mb-6">{t('pages.whitepaper.sections.problem.title')}</h2>
                    <p className="text-gray-300 mb-8 text-lg">{t('pages.whitepaper.sections.problem.intro')}</p>
                    <div className="grid gap-6">
                        {(t('pages.whitepaper.sections.problem.items', { returnObjects: true }) as any[]).map((item, idx) => (
                            <div key={idx} className="bg-surface p-6 rounded-xl border border-red-900/20">
                                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                                <p className="text-gray-400">{item.description}</p>
                            </div>
                        ))}
                    </div>
                    <p className="text-gray-400 mt-6 italic border-l-2 border-red-900/50 pl-4">{t('pages.whitepaper.sections.problem.conclusion')}</p>
                </div>

                {/* 2. Solution */}
                <div className="mb-16">
                    <h2 className="text-3xl font-bold text-gold mb-6">{t('pages.whitepaper.sections.solution.title')}</h2>
                    <p className="text-gray-300 mb-8 text-lg">{t('pages.whitepaper.sections.solution.intro')}</p>
                    <div className="bg-gradient-to-br from-surface to-green-900/10 p-8 rounded-2xl border border-green-900/30">
                        <ul className="space-y-4">
                            {(t('pages.whitepaper.sections.solution.items', { returnObjects: true }) as string[]).map((item, idx) => (
                                <li key={idx} className="flex items-start gap-4">
                                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center text-green-500 mt-1">✓</span>
                                    <span className="text-gray-200 text-lg">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <p className="text-gray-300 mt-6 font-medium">{t('pages.whitepaper.sections.solution.conclusion')}</p>
                </div>

                {/* 3. Core Philosophy */}
                <div className="mb-16">
                    <h2 className="text-3xl font-bold text-gold mb-6">{t('pages.whitepaper.sections.philosophy.title')}</h2>
                    <p className="text-gray-300 mb-8 text-lg">{t('pages.whitepaper.sections.philosophy.intro')}</p>
                    <div className="grid md:grid-cols-3 gap-6">
                        {(t('pages.whitepaper.sections.philosophy.items', { returnObjects: true }) as any[]).map((item, idx) => (
                            <div key={idx} className="bg-surface p-6 rounded-xl border border-gold/10 text-center hover:border-gold/30 transition-colors">
                                <h3 className="text-lg font-bold text-white mb-3 text-gold">{item.title}</h3>
                                <p className="text-sm text-gray-400">{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <Divider />

                {/* 4. Technology */}
                <div className="mb-16">
                    <h2 className="text-3xl font-bold text-gold mb-8">{t('pages.whitepaper.sections.technology.title')}</h2>
                    <div className="space-y-12">
                        {(t('pages.whitepaper.sections.technology.subsections', { returnObjects: true }) as any[]).map((sub, idx) => (
                            <div key={idx}>
                                <h3 className="text-2xl font-semibold text-white mb-4">{sub.title}</h3>
                                <p className="text-gray-300 mb-4">{sub.content}</p>
                                {sub.listTitle && <p className="text-gray-400 mb-2 font-medium">{sub.listTitle}</p>}
                                <ul className="grid md:grid-cols-2 gap-4">
                                    {sub.items.map((item: string, i: number) => (
                                        <li key={i} className="flex items-center gap-3 bg-surface/50 p-3 rounded-lg border border-gray-800">
                                            <span className="w-2 h-2 rounded-full bg-gold"></span>
                                            <span className="text-gray-300">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                                {sub.conclusion && <p className="text-gray-400 mt-4 italic">{sub.conclusion}</p>}
                            </div>
                        ))}
                    </div>
                </div>

                {/* 5. Trading & Liquidity */}
                <div className="mb-16">
                    <h2 className="text-3xl font-bold text-gold mb-6">{t('pages.whitepaper.sections.trading.title')}</h2>
                    <p className="text-gray-300 mb-6 text-lg">{t('pages.whitepaper.sections.trading.intro')}</p>
                    <div className="bg-surface p-8 rounded-2xl border border-gray-800">
                        <ul className="space-y-4 mb-6">
                            {(t('pages.whitepaper.sections.trading.items', { returnObjects: true }) as string[]).map((item, idx) => (
                                <li key={idx} className="flex items-center gap-3">
                                    <i className="fas fa-exchange-alt text-gold/50"></i>
                                    <span className="text-gray-300">{item}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="text-gray-400 border-t border-gray-800 pt-4">{t('pages.whitepaper.sections.trading.conclusion')}</p>
                    </div>
                </div>

                {/* 6. Utility */}
                <div className="mb-16">
                    <h2 className="text-3xl font-bold text-gold mb-6">{t('pages.whitepaper.sections.utility.title')}</h2>
                    <p className="text-gray-300 mb-6 text-lg">{t('pages.whitepaper.sections.utility.intro')}</p>
                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                        {(t('pages.whitepaper.sections.utility.items', { returnObjects: true }) as string[]).map((item, idx) => (
                            <div key={idx} className="bg-surface p-4 rounded-xl border border-gold/5 flex items-start gap-3">
                                <i className="fas fa-star text-gold mt-1"></i>
                                <span className="text-gray-300">{item}</span>
                            </div>
                        ))}
                    </div>
                    <p className="text-white font-medium text-center bg-gold/10 p-4 rounded-xl border border-gold/20">{t('pages.whitepaper.sections.utility.conclusion')}</p>
                </div>

                <Divider />

                {/* 7. Governance */}
                <div className="mb-16">
                    <h2 className="text-3xl font-bold text-gold mb-6">{t('pages.whitepaper.sections.governance.title')}</h2>
                    <p className="text-gray-300 mb-6">{t('pages.whitepaper.sections.governance.intro')}</p>
                    <div className="bg-surface/30 p-8 rounded-2xl border border-gray-800">
                        <p className="text-gold font-semibold mb-4">{t('pages.whitepaper.sections.governance.listTitle')}</p>
                        <ul className="space-y-3">
                            {(t('pages.whitepaper.sections.governance.items', { returnObjects: true }) as string[]).map((item, idx) => (
                                <li key={idx} className="flex items-center gap-3 text-gray-300">
                                    <i className="fas fa-check text-green-500"></i>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* 8. Roadmap (Summary) */}
                <div className="mb-16">
                    <h2 className="text-3xl font-bold text-gold mb-8">{t('pages.whitepaper.sections.roadmap.title')}</h2>
                    <div className="space-y-6">
                        {(t('pages.whitepaper.sections.roadmap.phases', { returnObjects: true }) as any[]).map((phase, idx) => (
                            <div key={idx} className="flex gap-6">
                                <div className="hidden md:flex flex-col items-center">
                                    <div className="w-8 h-8 rounded-full bg-gold/20 border border-gold flex items-center justify-center text-gold font-bold text-sm">
                                        {idx + 1}
                                    </div>
                                    <div className="w-px h-full bg-gray-800 my-2"></div>
                                </div>
                                <div className="flex-1 bg-surface p-6 rounded-xl border border-gray-800 hover:border-gold/30 transition-colors">
                                    <h3 className="text-xl font-bold text-white mb-4">{phase.title}</h3>
                                    <ul className="grid md:grid-cols-2 gap-3">
                                        {phase.items.map((item: string, i: number) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                                                <span className="text-gold mt-1">•</span>
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 9. Vision */}
                <div className="mb-16 text-center">
                    <h2 className="text-3xl font-bold text-gold mb-6">{t('pages.whitepaper.sections.vision.title')}</h2>
                    <p className="text-gray-300 text-lg mb-8">{t('pages.whitepaper.sections.vision.intro')}</p>
                    <div className="grid md:grid-cols-2 gap-6">
                        {(t('pages.whitepaper.sections.vision.items', { returnObjects: true }) as string[]).map((item, idx) => (
                            <div key={idx} className="bg-gradient-to-br from-gold/10 to-surface p-8 rounded-2xl border border-gold/20 flex items-center justify-center">
                                <p className="text-white font-medium text-lg leading-relaxed">"{item}"</p>
                            </div>
                        ))}
                    </div>
                </div>

                <Divider />

                {/* 10. Conclusion */}
                <div className="text-center max-w-3xl mx-auto">
                    <h2 className="text-3xl font-bold text-white mb-8">{t('pages.whitepaper.sections.conclusion.title')}</h2>
                    <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                        {(t('pages.whitepaper.sections.conclusion.content', { returnObjects: true }) as string[]).map((paragraph, idx) => (
                            <p key={idx} className={idx >= 3 ? "text-gold font-semibold text-xl" : ""}>{paragraph}</p>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-20 pt-10 border-t border-gray-800 text-center text-gray-500 text-sm">
                    <p>© 2026 IDNGOLD. All Rights Reserved. | Smart Billionaire Indonesia</p>
                </div>

            </div>
        </div>
    );
};
